import { Component, inject, OnInit } from '@angular/core';
import { RootState } from '@next/core-lib/app';
import { AuthorizationSelectors } from '@next/core-lib/authorization';
import { BrandingService } from '@next/core-lib/branding';
import { EnvironmentInjector } from '@next/core-lib/environment';
import { LocaleService } from '@next/core-lib/i18n';
import { NavigationActions, ParentNavigationItem } from '@next/core-lib/navigation';
import { AccessRoleTypes, UserSelectors } from '@next/core-lib/user';
import { Store } from '@ngrx/store';
import { combineLatest, skipWhile } from 'rxjs';

import { Environment } from 'src/environments/environment.model';

import { setTenant } from './branding/store/branding.actions';
import { ApplicationKeys } from './shared/models';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  standalone: false,
})
export class AppComponent implements OnInit {
  private readonly env = inject(EnvironmentInjector) as Environment;

  constructor(
    private readonly store: Store<RootState>,
    private readonly localeService: LocaleService,
    private readonly brandingService: BrandingService,
  ) {
    combineLatest([
      this.store.select(UserSelectors.selectUserApplications),
      this.store.select(AuthorizationSelectors.hasAtLeastAdministratorRole),
    ])
      .pipe(skipWhile(([accessRules]) => !accessRules))
      .subscribe(([accessRules, isAtLeastAdmin]) => {
        const showSustainability = accessRules.some(
          (rule) =>
            rule.ApplicationKey === ApplicationKeys.PoultryNextSustainability &&
            rule.RoleKey !== AccessRoleTypes.NotAuthorized,
        );

        const menuItems: ParentNavigationItem[] = [
          {
            relativeUrl: '/flock',
            label: this.localeService.translate('app.flock'),
            icon: 'dashboard',
          },
        ];

        if (this.env.USE_POULTRY_SERVICE && this.env.BREEDERS_ENABLED) {
          menuItems.push({
            relativeUrl: '/breeders',
            label: this.localeService.translate('app.breeders'),
            icon: 'dashboard',
          });
        }

        if (showSustainability && !this.brandingService.isTenant('Agravis')) {
          menuItems.push({
            label: this.localeService.translate('sustainability.sustainability'),
            icon: 'eco',
            children: [
              {
                relativeUrl: '/farm-resources',
                label: this.localeService.translate('sustainability.farm-resources'),
              },
              {
                relativeUrl: '/carbon-footprint',
                label: this.localeService.translate('sustainability.carbon-footprint'),
              },
            ],
          });
        }

        if (!this.brandingService.isTenant('Agravis')) {
          menuItems.push({
            relativeUrl: '/delivery',
            label: this.localeService.translate('delivery-title'),
            icon: 'local_shipping',
          });
        }

        menuItems.push({
          label: 'app.advices',
          icon: 'library_books',
          children: [
            {
              label: 'app.feed-advice',
              relativeUrl: '/feed-advice',
            },
          ],
        });

        if (isAtLeastAdmin) {
          menuItems.push({
            label: 'app.management',
            icon: 'tune',
            children: [
              {
                relativeUrl: '/product',
                label: 'app.products',
              },
              {
                relativeUrl: '/reference-scheme',
                label: 'app.reference-scheme',
              },
            ],
          });
        }

        menuItems.push({
          relativeUrl: '/settings',
          label: this.localeService.translate('app.settings'),
          icon: 'settings',
        });

        this.store.dispatch(NavigationActions.setNavigationItems({ navigationItems: menuItems }));
      });
  }

  ngOnInit(): void {
    this.initializeBranding();
  }

  private initializeBranding(): void {
    const tenantName = this.brandingService.isTenant('Agravis') ? 'Agravis' : 'Agrifirm';
    this.store.dispatch(setTenant({ tenantName }));
  }
}
